import { Cable, Check, Minus } from "lucide-react";

const METHODS = [
  ["obd", "OBD"],
  ["bench", "Bench"],
  ["boot", "Boot"],
];

/**
 * Published ECU tool compatibility for one vehicle, in three explicit states:
 *   SUPPORTED — VEHICLE VERIFIED : exact vehicle application matched in the
 *                                  manufacturer's published data
 *   SUPPORTED — ECU FAMILY       : exact vehicle not listed, but the same ECU
 *                                  family is supported elsewhere in that
 *                                  manufacturer's data (Bench/Boot from the
 *                                  family; OBD only where the source data
 *                                  establishes it applies generically)
 *   NOT LISTED                   : the ECU family does not appear anywhere in
 *                                  that manufacturer's data
 */
export function ToolCompatibility({ items, testPrefix = "tc" }) {
  if (!items || !items.length) return null;
  const supportedCount = items.filter((t) => t.supported).length;
  return (
    <section className="mt-10 border border-line" data-testid={`${testPrefix}-tool-compat`} data-compat-version="3">
      <div className="border-b border-line bg-surface px-6 py-5">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-regal-amber">
          <Cable size={14} /> Tuning tool compatibility
        </p>
        <p className="mt-2 text-sm text-neutral-500" data-testid={`${testPrefix}-tool-compat-summary`}>
          {supportedCount > 0
            ? `This ECU is listed in ${supportedCount} of 4 manufacturer compatibility databases.`
            : "This ECU was not found in the manufacturer compatibility databases we cross-reference."}
        </p>
      </div>
      <div className="grid gap-px bg-line sm:grid-cols-2 lg:grid-cols-4">
        {items.map((t) => (
          <div key={t.tool} className="bg-white p-5" data-testid={`${testPrefix}-tool-${t.tool}`}>
            <p className="font-display text-xs font-bold uppercase tracking-[0.12em] text-ink">{t.tool_name}</p>
            {t.supported ? (
              <>
                <p className={`mt-2 font-mono text-[10px] uppercase tracking-[0.14em] ${t.basis === "ecu_family" ? "text-amber-700" : "text-emerald-700"}`} data-testid={`${testPrefix}-tool-${t.tool}-state`}>
                  <Check size={11} className="mr-1 inline" />{t.basis === "ecu_family" ? "Supported — ECU family" : "Supported — vehicle verified"}
                </p>
                {(t.ecus || []).length > 0 && (
                  <p className="mt-2 text-xs text-neutral-500" data-testid={`${testPrefix}-tool-${t.tool}-ecu`}>
                    ECU: {t.ecus.join(" · ")}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {METHODS.map(([key, label]) =>
                    t[key] ? (
                      <span key={key} className="border border-emerald-700/30 bg-emerald-50 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-emerald-700" data-testid={`${testPrefix}-tool-${t.tool}-${key}`}>
                        {label}
                      </span>
                    ) : null
                  )}
                  {(t.other_methods || []).map((m) => (
                    <span key={m} className="border border-line bg-surface px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-neutral-500" data-testid={`${testPrefix}-tool-${t.tool}-other-${String(m).replace(/\W+/g, "-").toLowerCase().slice(0, 24)}`}>
                      {m}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <div className="mt-2" data-testid={`${testPrefix}-tool-${t.tool}-state`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-neutral-500">
                  <Minus size={11} className="mr-1 inline text-neutral-400" />Not listed
                </p>
                <p className="mt-1.5 text-xs leading-relaxed text-neutral-500">
                  Not found in this manufacturer&apos;s published data — ask us, coverage updates frequently.
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
